import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  EMERGENCY_REPORT_TYPES,
  EMERGENCY_SEVERITIES,
  formatEmergencyCoordinates,
  getEmergencySeverityBadgeClass,
  getEmergencySeverityLabel,
} from "@/lib/emergency";

const INCIDENT_TYPE_GROUPS = [
  {
    label: "Medical And Rescue",
    values: ["medical", "stranded_driver", "pharmacy_need", "shelter_request"],
  },
  {
    label: "Crash And Roadway",
    values: ["collision", "rollover", "road_hazard", "traffic_blockage", "mechanical"],
  },
  {
    label: "Safety And Security",
    values: ["fire", "hazmat", "police", "assault", "cargo_theft"],
  },
  {
    label: "Infrastructure And Weather",
    values: ["weather", "flooding", "power_outage", "other"],
  },
];

const SEVERITY_HELP_TEXT = {
  advisory: "Minor issue or situational awareness report.",
  low: "Limited impact, still needs attention.",
  medium: "Operational disruption or developing risk.",
  high: "Serious incident needing fast response.",
  urgent: "Immediate response recommended now.",
  critical: "Life-safety or major multi-unit emergency.",
};

const EmergencyReportDialog = ({
  open,
  onOpenChange,
  form,
  onChange,
  onSubmit,
  submitting = false,
  currentCoords,
  selectedCoords,
  placingOnMap = false,
  onUseCurrentLocation,
  onStartMapPick,
}) => {
  const activeCoords = selectedCoords || currentCoords;
  const hasCoords = Boolean(
    Number.isFinite(Number(activeCoords?.latitude ?? activeCoords?.lat)) &&
    Number.isFinite(Number(activeCoords?.longitude ?? activeCoords?.lng)),
  );
  const descriptionLength = String(form?.description || "").trim().length;
  const canSubmit = hasCoords && form?.report_type && form?.severity && descriptionLength >= 8 && !submitting;

  const handleChange = (field, value) => {
    if (typeof onChange === "function") {
      onChange(field, value);
    }
  };

      const selectedSeverityHelp = SEVERITY_HELP_TEXT[form?.severity] || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="mx-2 flex max-h-[92vh] w-[calc(100vw-1rem)] max-w-xl flex-col gap-0 overflow-hidden border-stone-700/80 bg-stone-950 p-0 text-stone-100 shadow-[0_0_35px_rgba(249,115,22,0.12)]">
        <DialogHeader className="border-b border-stone-800/80 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-orange-100">Emergency Incident Report</DialogTitle>
              <DialogDescription className="mt-1 text-stone-400">
                Pin the map location, capture severity clearly, and send an operational report without leaving navigation.
              </DialogDescription>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 border-stone-700 bg-black/40 text-stone-100 hover:bg-stone-900"
              onClick={() => onOpenChange?.(false)}
            >
              Close
            </Button>
          </div>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 space-y-5 overflow-y-scroll overscroll-contain px-5 py-4 pr-4 [scrollbar-width:thin]"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="rounded-xl border border-stone-700/80 bg-black/40 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="border-orange-500/40 bg-orange-500/10 text-orange-100">Location</Badge>
              {selectedCoords ? (
                <Badge className="border-cyan-500/40 bg-cyan-500/10 text-cyan-100">Map Pin Selected</Badge>
              ) : currentCoords ? (
                <Badge className="border-emerald-500/40 bg-emerald-500/10 text-emerald-100">Using Current Position</Badge>
              ) : (
                <Badge className="border-amber-500/40 bg-amber-500/10 text-amber-100">Coordinates Needed</Badge>
              )}
              {placingOnMap && (
                <Badge className="border-orange-500/40 bg-orange-500/15 text-orange-50">Tap The Map To Drop A Pin</Badge>
              )}
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="emergency-report-lat">Latitude</Label>
                <Input
                  id="emergency-report-lat"
                  readOnly
                  value={hasCoords ? Number(activeCoords.latitude ?? activeCoords.lat).toFixed(5) : ""}
                  placeholder="Waiting for map coordinates"
                  className="border-stone-700 bg-stone-900/90"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency-report-lng">Longitude</Label>
                <Input
                  id="emergency-report-lng"
                  readOnly
                  value={hasCoords ? Number(activeCoords.longitude ?? activeCoords.lng).toFixed(5) : ""}
                  placeholder="Waiting for map coordinates"
                  className="border-stone-700 bg-stone-900/90"
                />
              </div>
            </div>

            <p className="mt-3 text-xs text-stone-400">
              {formatEmergencyCoordinates(activeCoords)}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="border-stone-700 bg-black/40 text-stone-100 hover:bg-stone-900"
                onClick={onUseCurrentLocation}
                disabled={submitting}
              >
                Use Current Location
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-orange-500/40 bg-orange-500/10 text-orange-50 hover:bg-orange-500/20"
                onClick={onStartMapPick}
                disabled={submitting}
              >
                {placingOnMap ? "Pick A Point On The Map" : "Select On Map"}
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="emergency-report-type">Incident Type</Label>
              <Select
                value={form?.report_type || ""}
                onValueChange={(value) => handleChange("report_type", value)}
                disabled={submitting}
              >
                <SelectTrigger id="emergency-report-type" className="border-stone-700 bg-stone-900/90">
                  <SelectValue placeholder="Select incident type" />
                </SelectTrigger>
                <SelectContent className="border-stone-700 bg-stone-950 text-stone-100">
                  {INCIDENT_TYPE_GROUPS.map((group, groupIndex) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel className="text-xs uppercase tracking-wider text-stone-400">{group.label}</SelectLabel>
                      {EMERGENCY_REPORT_TYPES.filter((type) => group.values.includes(type.value)).map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                      {groupIndex < INCIDENT_TYPE_GROUPS.length - 1 && <SelectSeparator className="bg-stone-800" />}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergency-report-severity">Severity</Label>
              <Select
                value={form?.severity || ""}
                onValueChange={(value) => handleChange("severity", value)}
                disabled={submitting}
              >
                <SelectTrigger id="emergency-report-severity" className="border-stone-700 bg-stone-900/90">
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent className="border-stone-700 bg-stone-950 text-stone-100">
                  {EMERGENCY_SEVERITIES.map((severity) => (
                    <SelectItem key={severity.value} value={severity.value}>
                      {severity.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form?.severity && (
                <div className="space-y-2">
                  <Badge className={getEmergencySeverityBadgeClass(form.severity)}>
                    {getEmergencySeverityLabel(form.severity)}
                  </Badge>
                  {selectedSeverityHelp && (
                    <p className="text-xs text-stone-400">{selectedSeverityHelp}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="emergency-report-description">Operational Notes</Label>
            <Textarea
              id="emergency-report-description"
              value={form?.description || ""}
              onChange={(event) => handleChange("description", event.target.value)}
              placeholder="Describe what is happening, blocked roads, visible hazards, injuries, or access constraints."
              rows={5}
              disabled={submitting}
              className="border-stone-700 bg-stone-900/90"
            />
            <div className="flex items-center justify-between text-xs">
              <span className="text-stone-400">
                Include enough detail for dispatch or responders to act without a follow-up call.
              </span>
              <span className={descriptionLength >= 8 ? "text-emerald-300" : "text-amber-200"}>
                {descriptionLength}/8 min
              </span>
            </div>
          </div>

          {!hasCoords && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
              Select a point on the map or use your current location before submitting the report.
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-stone-800/80 px-5 py-4 gap-2 sm:justify-between">
          <p className="text-xs text-stone-400">
            Reports save with coordinates, severity, and timestamp for routing and operational review.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-stone-700 bg-black/40 text-stone-100 hover:bg-stone-900"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-orange-600 text-black hover:bg-orange-500"
              disabled={!canSubmit}
              onClick={onSubmit}
            >
              {submitting ? "Submitting..." : "Submit Emergency Report"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmergencyReportDialog;
